import React from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const RAG_COLORS = {
  RED: "#D32F2F",
  AMBER: "#F57C00",
  YELLOW: "#FBC02D",
  GREEN: "#388E3C",
  TEAL: "#00897B",
};

export const RAG_BG = {
  RED: "bg-[#D32F2F]/10 text-[#D32F2F]",
  AMBER: "bg-[#F57C00]/10 text-[#F57C00]",
  YELLOW: "bg-[#FBC02D]/15 text-[#B8860B]",
  GREEN: "bg-[#388E3C]/10 text-[#388E3C]",
  TEAL: "bg-[#00897B]/10 text-[#00897B]",
};

export function ragColor(rag) {
  return RAG_COLORS[rag] || RAG_COLORS.AMBER;
}

export function priorityFor(pillar) {
  if (pillar?.priority) return pillar.priority;
  const rag = pillar?.rag;
  if (rag === "RED") return "0–30 days";
  if (rag === "AMBER") return "30–90 days";
  if (rag === "YELLOW" || rag === "TEAL") return "90–180 days";
  return "Maintain";
}

// Non-scoring workflow pillars — behave differently from the 12 assessment pillars.
// P9 = SDF Readiness Screening (score sourced from SDF Module B)
// P10 = Evidence Planner, P11 = Readiness Roadmap (workflow deliverables)
export const WORKFLOW_PILLAR_CODES = new Set(["P9", "P10", "P11"]);
export const isWorkflowPillar = (code) => WORKFLOW_PILLAR_CODES.has(code);

// Rule #4/#12: P9 shows SDF Module B score, never pillars.P9.pct
export function effectiveScore(pillar, sdf) {
  if (pillar?.code === "P9") return sdf?.module_b_score ?? null;
  return pillar?.pct ?? null;
}

export function effectiveRag(pillar, sdf) {
  if (pillar?.code === "P9" && sdf?.module_b_rag) return sdf.module_b_rag;
  return pillar?.rag || "AMBER";
}

export function pillarsArray(pillarsDict) {
  if (!pillarsDict) return [];
  return Object.keys(pillarsDict)
    .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10))
    .map((k) => pillarsDict[k]);
}

export function useIsMobile() {
  const [mobile, setMobile] = React.useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = (e) => setMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return mobile;
}

export const InfoTip = ({ text, testId }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testId || "info-tip"}
          className="text-slate-400 hover:text-[#008080] transition-colors"
          aria-label="Metric info"
        >
          <Info size={15} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-xs leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const GrcCard = ({ title, info, infoTestId, children, testId, className = "" }) => (
  <div
    data-testid={testId}
    className={`bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}
  >
    <div className="flex items-start justify-between mb-3">
      <h3 className="text-sm font-semibold text-[#003366]">{title}</h3>
      {info && <InfoTip text={info} testId={infoTestId} />}
    </div>
    {children}
  </div>
);

// --- RBAC (basic) -----------------------------------------------------------
import { hasRole } from "@/context/RoleContext";
export const canEditFindings = (role) => hasRole(role, "compliance_officer");
export const canRetestControls = (role) => hasRole(role, "control_owner");

export function daysRemaining(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

export function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// "14 Jul 2026, 3:24 PM"
export function fmtDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date}, ${time}`;
}
